import type { Item, ItemCustomFieldValue } from 'src/lib/api/types/items';
import type { ISwimlaneHeader, IVisualizationHeader } from '@/lib/api/types/roadmapping';

/**
 * Get the field ID string from a swimlane header.
 * Used for determining which field to use for coloring and filtering.
 *
 * @param header - The swimlane header to extract the field ID from
 * @returns The field ID string, or null if header is null
 */
export function getHeaderFieldId(header: ISwimlaneHeader | IVisualizationHeader | null): string {
  if (!header) {
    return '';
  }

  if (header.type === 'ParentItemSelector') {
    return 'parentItem';
  }

  if (header.type === 'DateSelector') {
    return 'itemDate';
  }

  if (header.type === 'CustomPropertySelectorByID') {
    return header.customPropertyID;
  }

  if (header.type === 'createdBy') {
    return 'createdBy';
  }

  return '';
}

export const extractHeaderKeyFromCustomFieldValue = (value: ItemCustomFieldValue | undefined) => {
  return typeof value === 'number' || typeof value === 'boolean' || typeof value === 'string'
    ? String(value)
    : 'null';
};

export function getHeaderItemKey(
  headerName: string | null,
): keyof Item | `customFields.${string}` | null {
  // Assume a custom field if the headerName is a numeric string (custom field IDs are numeric)
  if (headerName && Number.isInteger(Number(headerName))) {
    return `customFields.${headerName}`;
  }
  switch (headerName) {
    case 'status':
      return 'itemStatusId';
    case 'itemDate':
      return 'targetStartDateTime';
    case 'parentItem':
      return 'parentId';
    case 'createdBy':
      return 'createdBy';
    case 'team':
      return 'teamId';
    case 'estimationId':
      return 'estimationId';
    default:
      return null;
  }
}

/**
 * Maps a layout header field value to the corresponding hiddenFieldId
 * used in the published visualization config.
 *
 * This is the canonical mapping between frontend field identifiers and
 * backend hidden field IDs for published views.
 *
 * @param header - The header field value (e.g., 'status', 'team', 'parentItem', or custom field ID)
 * @returns The corresponding hidden field ID, or null if not a hideable field
 */
export function mapHeaderToHiddenFieldId(header: string | null): string | null {
  if (!header) return null;
  if (header === 'status') return 'status';
  if (header === 'team') return 'team';
  if (header === 'parentItem') return 'parent';
  if (header === 'createdBy') return 'createdBy';
  if (header === 'estimationId') return 'estimation';
  if (/^\d+$/.test(header)) return header;
  return null;
}
